import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SkipLink } from "@/components/skip-link";
import logoColorWebp from "@assets/Ben Boaz Transport Logo_1764521167070.webp";
import logoWhiteWebp from "@assets/Ben Boaz Transport Logo - w_1764522353654.webp";
import logoColorPng from "@assets/Ben Boaz Transport Logo_1764521167070.png";
import logoWhitePng from "@assets/Ben Boaz Transport Logo - w_1764522353654.png";

export function Navigation() {
  const [, setLocation] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = (path: string) => {
    setLocation(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <SkipLink />
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-background/95 backdrop-blur-md shadow-lg" : "bg-primary"
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-28">
            <button
              onClick={() => navigateTo("/")}
              className="hover:opacity-90 transition-opacity flex-shrink-0 py-2"
              data-testid="link-home"
              aria-label="Ben Boaz Transport Home"
            >
              <picture>
                <source 
                  srcSet={isScrolled ? logoColorWebp : logoWhiteWebp} 
                  type="image/webp" 
                />
                <img
                  src={isScrolled ? logoColorPng : logoWhitePng}
                  alt="Ben Boaz Transport LLC"
                  width={324}
                  height={108}
                  loading="eager"
                  className="w-auto object-contain transition-all duration-300"
                  style={{ height: "108px", maxWidth: "324px" }}
                />
              </picture>
            </button>

            <div className="hidden xl:flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigateTo("/")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-home-nav"
              >
                Home
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/careers")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-careers"
              >
                Careers
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/services")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-services"
              >
                Services
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/fleet")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-fleet"
              >
                Fleet
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/about")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-about"
              >
                About
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/blog")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-blog"
              >
                Blog
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigateTo("/contact")}
                className={`text-base font-medium ${isScrolled ? "text-foreground" : "text-white hover:text-white"}`}
                data-testid="link-contact"
              >
                Contact
              </Button>
            </div>

            <div className="flex items-center gap-2 lg:gap-3">
              <Badge
                className="hidden lg:flex bg-yellow-400 hover:bg-yellow-400 text-black font-bold uppercase text-xs px-3 py-1.5 gap-2 no-default-hover-elevate no-default-active-elevate animate-echo cursor-pointer shadow-lg"
                onClick={() => navigateTo("/careers#application")}
                data-testid="badge-hiring"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                </span>
                Now Hiring CDL Drivers
              </Badge>

              <Button
                variant={isScrolled ? "default" : "secondary"}
                size="sm"
                onClick={() => navigateTo("/contact")}
                className="hidden lg:flex bg-accent hover:bg-accent text-accent-foreground font-semibold"
                data-testid="button-freight-quote"
              >
                REQUEST FREIGHT QUOTE
              </Button>

              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="xl:hidden"
                    aria-label="Toggle menu"
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                    data-testid="button-menu-toggle"
                  >
                    {isMobileMenuOpen ? (
                      <X className={`h-6 w-6 ${isScrolled ? "text-foreground" : "text-white"}`} />
                    ) : (
                      <Menu className={`h-6 w-6 ${isScrolled ? "text-foreground" : "text-white"}`} />
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" id="mobile-menu" data-testid="mobile-menu">
                  <div className="flex flex-col gap-3 mt-8">
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-home"
                    >
                      Home
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/careers")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-careers"
                    >
                      Careers
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/services")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-services"
                    >
                      Services
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/fleet")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-fleet"
                    >
                      Fleet
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/about")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-about"
                    >
                      About
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/blog")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-blog"
                    >
                      Blog
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigateTo("/contact")}
                      className="justify-start text-lg h-12 w-full"
                      data-testid="mobile-link-contact"
                    >
                      Contact
                    </Button>
                    <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                      <Button
                        onClick={() => navigateTo("/contact")}
                        className="w-full bg-accent hover:bg-accent text-accent-foreground font-bold"
                        data-testid="mobile-button-quote"
                      >
                        REQUEST FREIGHT QUOTE
                      </Button>
                      <Button
                        onClick={() => navigateTo("/careers#application")}
                        variant="outline"
                        className="w-full font-bold"
                        data-testid="mobile-button-apply"
                      >
                        APPLY NOW
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
